import { LocalizedText, PartFormData } from "./types";

export const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

// Read the text for a locale. Returns "" when the locale has no content.
export const readLocale = (value: LocalizedText | undefined, locale: string): string => value?.[locale] ?? "";

// Set the text for a locale, returning a new LocalizedText.
export const writeLocale = (value: LocalizedText, locale: string, text: string): LocalizedText => ({
  ...value,
  [locale]: text,
});

/**
 * Describes one translatable text field within a lesson: both its dot-path
 * (for form.setValue / form.getValues) and a human-readable label (for
 * validation messages). Walking the tree once centralises the form shape so
 * validators and auto-translators can't drift out of sync.
 */
export type TranslatableField = {
  path: string;
  label: string;
  value: LocalizedText | undefined;
};

export const walkTranslatableFields = (parts: PartFormData[] | undefined): TranslatableField[] => {
  const fields: TranslatableField[] = [];
  (parts ?? []).forEach((part, pi) => {
    const p = `Part ${pi + 1}`;
    fields.push({ path: `parts.${pi}.title`, label: `${p} — title`, value: part.title });

    (part.content ?? []).forEach((block, bi) => {
      if (block.type === "image") return; // URLs, not translatable text
      fields.push({
        path: `parts.${pi}.content.${bi}.content`,
        label: `${p} — content block ${bi + 1} (${block.type})`,
        value: block.content,
      });
    });

    (part.questions ?? []).forEach((q, qi) => {
      const qp = `${p} — Question ${qi + 1}`;
      fields.push({ path: `parts.${pi}.questions.${qi}.title`, label: `${qp} — title`, value: q.title });
      fields.push({
        path: `parts.${pi}.questions.${qi}.tipDescription`,
        label: `${qp} — tip`,
        value: q.tipDescription,
      });
      (q.answers ?? []).forEach((a, ai) => {
        fields.push({
          path: `parts.${pi}.questions.${qi}.answers.${ai}.text`,
          label: `${qp} — answer ${ai + 1}`,
          value: a.text,
        });
      });
    });
  });
  return fields;
};

